import { faker } from '@faker-js/faker';

import { coerceBoolean, random } from '@hsp/utilities';

import { defaultMockTime } from 'constants/defaults';
import { Uri } from 'constants/uri';
import { IResult, IServerSidePagingResult } from 'types/api/common';
import { ExternalListByClient, IExternalList } from 'types/api/external-list';

import { BaseHttpService } from './http/base-http.service';

class ExternalListService extends BaseHttpService {
  constructor() {
    super({ endpoints: [Uri.ExternalList] });
  }

  deleteExternalList(id: string, cancel = false): Promise<IResult<IExternalList>> {
    const url = `${Uri.ExternalList}/${id}`;

    return this.delete(this.defaultRequestConfig(url, cancel));
  }

  generateSecretKey(id: string, cancel = false): Promise<IResult<string>> {
    const url = `${Uri.ExternalList}/${id}/${Uri.SecretKey}`;

    return this.get(this.defaultRequestConfig(url, cancel));
  }

  getByClient(
    { clientIds, statusIds, sort, userId, showAll, page, pageSize, filter }: ExternalListByClient,
    cancel = false
  ): Promise<IServerSidePagingResult<IExternalList[]>> | undefined {
    const url = Uri.ExternalList;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { clientIds, filter, page: page ?? -1, pageSize, showAll, sort, statusIds, userId },
    });
  }

  getExternalList(id: string, cancel = false): Promise<IResult<IExternalList>> {
    const url = `${Uri.ExternalList}/${id}`;

    return this.get(this.defaultRequestConfig(url, cancel));
  }

  getExternalLists(userId?: string, cancel = false): Promise<IResult<IExternalList[]>> {
    const url = Uri.ExternalList;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { page: -1, userId },
    });
  }

  updateExternalList(listId: string, formData: FormData, file?: File, cancel = false): Promise<IResult<IExternalList>> {
    const url = `${Uri.ExternalList}/${listId}`;

    return this.upload(file, { formData, method: 'PUT' }, this.defaultRequestConfig(url, cancel));
  }

  uploadExternalList(file: File, formData: FormData, cancel = false): Promise<IResult<IExternalList>> {
    const url = Uri.ExternalList;

    return this.upload(file, { formData }, this.defaultRequestConfig(url, cancel));
  }
}

export const externalListService = new ExternalListService();

if (coerceBoolean(process.env.REACT_APP_API_AUDIENCE_FAKE)) {
  const mocked = externalListService.mock(defaultMockTime);
  const mock = mocked?.adapter;

  mock?.onGet(Uri.ExternalList).reply((): Promise<[number, IResult<IExternalList[]>]> => {
    return new Promise((resolve) => {
      const data: IExternalList[] = [];

      Array.from({ length: random(50) }).forEach(() => {
        data.push({
          id: faker.string.uuid(),
          name: faker.word.noun(),
          status: { id: faker.number.int(), name: faker.random.word() },
          userId: faker.string.uuid(),
        });
      });

      const result: IResult<IExternalList[]> = {
        dataContainer: data as unknown as IExternalList[],
        failure: false,
        statusCode: 200,
        success: true,
      };

      resolve([200, result]);
    });
  });
}
